import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import AuthService from './AuthService';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const authServiceRef = useRef(null);
  if (!authServiceRef.current) {
    authServiceRef.current = new AuthService();
  }
  const authService = authServiceRef.current;

  // This function handles login using a code (from the URL)
  const loginWithCode = useCallback(async (code, state) => {
    try {
      // Set a flag to indicate we're in the process of logging in
      localStorage.setItem('_login_in_progress', 'true');
      
      const userInfo = await authService.exchangeCodeForTokens(code, state);
      if (!userInfo) {
        throw new Error('Failed to get user information from token');
      }
      localStorage.setItem('userName', userInfo.userName);
      localStorage.setItem('userId', userInfo.userId);
      setUser(userInfo);
      
      // Clear the login in progress flag after a short delay
      setTimeout(() => {
        localStorage.removeItem('_login_in_progress');
      }, 2000);
      
      return userInfo;
    } catch (error) {
      localStorage.removeItem('_login_in_progress');
      console.error('Login failed:', error);
      throw error;
    }
  }, [authService]);

  const logout = () => {
    // Use AuthService logout which handles Azure AD logout redirect
    authService.logout();
    setUser(null);
  };

  /** Clear app session without redirecting to Azure (invalid/expired refresh, bad tokens). */
  const clearSession = useCallback(() => {
    authService.logoutLocal();
    setUser(null);
  }, [authService]);

  /** Try to restore/refresh session; updates context user on success. */
  const refreshSession = useCallback(async () => {
    try {
      const userInfo = await authService.refreshIfNeeded();
      if (userInfo) {
        localStorage.setItem('userName', userInfo.userName);
        localStorage.setItem('userId', userInfo.userId);
        setUser(userInfo);
      } else {
        setUser(null);
      }
      return userInfo;
    } catch (e) {
      console.error('refreshSession failed:', e);
      setUser(null);
      return null;
    }
  }, [authService]);

  // Auto login on load if token exists and refresh if needed
  useEffect(() => {
    const initAuth = async () => {
      // Check if there's an OAuth code in the URL - if so, skip initAuth
      // The App component will handle the login via loginWithCode
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('code')) {
        // OAuth callback in progress, skip initialization
        setLoading(false);
        return;
      }
      
      setLoading(true);
      try {
        // Skip initialization if login is in progress (prevents duplicate token requests)
        const loginInProgress = localStorage.getItem('_login_in_progress');
        if (loginInProgress === 'true') {
          // Wait a bit for login to complete
          await new Promise(resolve => setTimeout(resolve, 500));
          // If user info is already available, use it and skip token validation
          const userInfo = authService.getUserInfo();
          if (userInfo) {
            setUser(userInfo);
            setLoading(false);
            return;
          }
        }
        
        // Check if token exists
        const token = localStorage.getItem('access_token');
        
        if (token) {
          // Check if token is expired (Azure AD uses JWT expiration)
          const isExpired = await authService.isTokenExpired(token);
          
          if (isExpired) {
            try {
              const userInfo = await authService.refreshIfNeeded();

              if (userInfo) {
                localStorage.setItem('userName', userInfo.userName);
                localStorage.setItem('userId', userInfo.userId);
                setUser(userInfo);
              } else {
                clearSession();
              }
            } catch (error) {
              console.error('Token refresh failed:', error);
              clearSession();
            }
          } else {
            const userInfo = authService.getUserInfo();
            if (userInfo) {
              localStorage.setItem('userName', userInfo.userName);
              localStorage.setItem('userId', userInfo.userId);
              setUser(userInfo);
            } else {
              clearSession();
            }
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        clearSession();
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  return (
    <AuthContext.Provider value={{ user, loginWithCode, logout, loading, refreshSession, clearSession }}>
      {children}
    </AuthContext.Provider>
  );
};